import { useEffect, useState } from 'react'
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import axios from "axios"

import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'

import { setToken } from "@/auth/authSlice"
import { API_BASE_URL } from "@/config"

const request = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    paramsSerializer: {indexes: null},
})

const requestSilent = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    paramsSerializer: {indexes: null},
})

const requestSilentNoRetry = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    paramsSerializer: {indexes: null},
})

function setAuthHeader(token) {
    request.defaults.headers.common['Authorization'] = token
    requestSilent.defaults.headers.common['Authorization'] = token
}

function clearAuthHeader() {
    request.defaults.headers.common['Authorization'] = ""
    requestSilent.defaults.headers.common['Authorization'] = ""
}

function Api() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [message, setMessage] = useState("")
    let isRefreshing = false
    const requestQueue = []

    const handle401Error = async (error, axiosInstance, silent) => {
        const isRetry = error.config._retry

        if (!isRetry) {
            if (isRefreshing) {
                // Queue requests to retry after token refreshed
                return new Promise((resolve) => {
                    requestQueue.push((newToken) => {
                        error.config.headers["Authorization"] = newToken
                        resolve(axiosInstance(error.config))
                    })
                })
            }

            error.config._retry = true
            isRefreshing = true

            try {
                const refreshResponse = await axios.post(`${API_BASE_URL}/refresh-token`, {}, { withCredentials: true })
                const newAccessToken = refreshResponse.data
                dispatch(setToken(newAccessToken))

                // Retry the original request first
                error.config.headers["Authorization"] = newAccessToken
                const originalRequestResponse = await axiosInstance(error.config)

                // Process any queued requests
                requestQueue.forEach((callback) => callback(newAccessToken))
                requestQueue.length = 0
                isRefreshing = false

                return originalRequestResponse
            } catch (refreshError) {
                requestQueue.length = 0
                isRefreshing = false

                if (!silent) {
                    setMessage("Your session has expired. Please log in again.")
                    navigate("/logout")
                }
                return Promise.reject(refreshError)
            }
        }

        return Promise.reject(error)
    }

    useEffect(() => {
        request.interceptors.response.use((response) => response, async (error) => {
            const statusCode = error.response ? error.response.status : null

            if (statusCode === 401) {
                return handle401Error(error, request, false)
            }
            else if (statusCode === 403) {
                setMessage("You do not have access to this resource.")
            }
            else if (statusCode === 404) {
                setMessage("The requested resource was not found.")
            }
        
            return Promise.reject(error)
        })

        requestSilent.interceptors.response.use((response) => response, async (error) => {
            const statusCode = error.response ? error.response.status : null

            if (statusCode === 401) {
                return handle401Error(error, requestSilent, true)
            }
        
            return Promise.reject(error)
        })
    }, [])

    const handleClose = () => {
        setMessage("")
    }

    return (
        <Snackbar
            anchorOrigin={{vertical: "top", horizontal: "center" }}
            open={message != ""}
            onClose={handleClose}
        >
            <Alert
                variant="filled"
                severity="error"
                sx={{ width: '100%' }}
                onClose={handleClose}
            >
                { message }
            </Alert>
        </Snackbar>
    )
}

const error422Handler = (rawError, callback) => {
    if (rawError.response && rawError.response.status === 422) {
        const detail = rawError.response?.data?.detail
        if (detail) {
            const parsedError = {}
            detail.forEach(error => {
                const errorField = error.loc[error.loc.length - 1]
                parsedError[errorField] = error.msg
            })
            callback(parsedError)
        }
    }
}

const error400Handler = (rawError, callback) => {
    if (rawError.response && rawError.response.status === 400) {
        if (rawError.response.data && rawError.response.data.detail) {
            const detail = rawError.response.data.detail
            callback(detail)
        }
    }
}

export { Api as default, setAuthHeader, clearAuthHeader, request, requestSilent, error422Handler, error400Handler, requestSilentNoRetry }
